import type { FieldNameOf } from '@moment/sorm-client-data-access/schema'
import type { ReferenceDataField } from '@moment/trading-types/instrument-data'

export type SecurityField = FieldNameOf<'Security'>

type SharedField = ReferenceDataField & SecurityField

/** Fails to compile until every field both sides name the same is listed. */
function everySharedField<const Fields extends ReadonlyArray<SharedField>>(
  fields: Fields &
    ([Exclude<SharedField, Fields[number]>] extends [never]
      ? unknown
      : { missingSharedFields: Exclude<SharedField, Fields[number]> }),
): Fields {
  return fields
}

const RENAMED_FIELDS: ReadonlyArray<[ReferenceDataField, SecurityField]> = [
  ['isin', 'security_id'],
  ['type', 'asset_class'],
  ['instrument_class', 'asset_subtype'],
  ['issue_minimum_denomination', 'issue_minimum'],
  ['updated_at', 'reference_data_updated_at'],
  // Live inventory serves its mid eval as the eval itself.
  ['eval_price', 'mpe_price'],
  ['eval_ytw', 'mpe_ytw'],
  ['eval_ytm', 'mpe_ytm'],
  ['mid_price', 'mpe_price'],
  ['mid_ytw', 'mpe_ytw'],
  ['mid_ytm', 'mpe_ytm'],
  ['mid_ytc', 'mpe_mid_ytc'],
  ['mid_oas', 'mpe_mid_oas'],
  ['mid_g_spread', 'mpe_mid_g_spread'],
  ['mid_i_spread', 'mpe_mid_i_spread'],
  ['mid_z_spread', 'mpe_mid_z_spread'],
  ['mid_ytw_spread_bps', 'mpe_mid_ytw_spread_bps'],
  ['bid_price', 'mpe_bid_price'],
  ['bid_ytw', 'mpe_bid_ytw'],
  ['bid_ytm', 'mpe_bid_ytm'],
  ['bid_ytc', 'mpe_bid_ytc'],
  ['bid_oas', 'mpe_bid_oas'],
  ['bid_g_spread', 'mpe_bid_g_spread'],
  ['bid_i_spread', 'mpe_bid_i_spread'],
  ['bid_z_spread', 'mpe_bid_z_spread'],
  ['bid_ytw_spread_bps', 'mpe_bid_ytw_spread_bps'],
  ['ask_price', 'mpe_ask_price'],
  ['ask_ytw', 'mpe_ask_ytw'],
  ['ask_ytm', 'mpe_ask_ytm'],
  ['ask_ytc', 'mpe_ask_ytc'],
  ['ask_oas', 'mpe_ask_oas'],
  ['ask_g_spread', 'mpe_ask_g_spread'],
  ['ask_i_spread', 'mpe_ask_i_spread'],
  ['ask_z_spread', 'mpe_ask_z_spread'],
  ['ask_ytw_spread_bps', 'mpe_ask_ytw_spread_bps'],
  ['effective_duration', 'mpe_effective_duration'],
  ['macaulay_duration', 'mpe_macaulay_duration'],
  ['benchmark_isin', 'mpe_benchmark_isin'],
  ['benchmark_yield', 'mpe_benchmark_yield'],
]

const SAME_NAME_FIELDS = everySharedField([
  'accrued_interest',
  'accrued_interest_date',
  'accrued_interest_pop',
  'amount_outstanding',
  'amount_outstanding_date',
  'ask_eval_price_diff',
  'accretion_compounding_method',
  'ask_eval_ytw_diff',
  'asset_type',
  'bank_qualified',
  'bid_eval_price_diff',
  'bid_eval_ytw_diff',
  'bond_form_type',
  'bond_issuer_rv_score',
  'bond_warrant',
  'bullet',
  'callable',
  'call_frequency',
  'call_notification_convention',
  'call_notification_max',
  'call_notification_min',
  'call_announced',
  'call_type',
  'close_price',
  'close_price_asof',
  'close_ytm',
  'close_ytw',
  'coco_event_type',
  'coco_indicator',
  'coco_trigger_action',
  'coco_trigger_level_type',
  'coco_trigger_level_value',
  'coco_trigger_type',
  'contingent_interest_indicator',
  'continuously_callable_flag',
  'convertible',
  'convexity',
  'country_domicile',
  'country_issue',
  'country_of_risk',
  'coupon',
  'coupon_frequency',
  'coupon_inflation_protected',
  'coupon_subtype',
  'coupon_summary',
  'coupon_type',
  'coupon_type_method',
  'covered_bond_collateral',
  'credit_quality_class',
  'credit_sensitive_indicator',
  'currency',
  'currently_callable',
  'default_indicator',
  'discretionary_coupon',
  'cusip',
  'dated_date',
  'daycount',
  'de_minimis_threshold',
  'days_to_maturity_from_next_call',
  'description',
  'description_short',
  'duration',
  'duration_date',
  'duration_times_spread',
  'effective_call_date',
  'effective_call_price',
  'emerging_status',
  'expected_spread',
  'fdic_code',
  'fdic_insured',
  'first_coupon_date',
  'fitch_outlook',
  'fitch_outlook_date',
  'fitch_rating',
  'fitch_rating_date',
  'fitch_underlying_rating',
  'fitch_underlying_rating_date',
  'float_benchmark',
  'float_formula',
  'float_formula_reset_date',
  'float_max_rate',
  'float_min_rate',
  'float_reset_frequency',
  'float_spread',
  'gics_sector_1',
  'gics_sector_2',
  'gics_sector_3',
  'gics_sector_4',
  'has_extraordinary_call',
  'insured',
  'insurer',
  'is_144a',
  'is_amt_applicable',
  'is_pre_refunded',
  'is_state_tax_applicable',
  'is_structured_product',
  'ice_sector_1',
  'ice_sector_2',
  'ice_sector_3',
  'ice_sector_4',
  'impact_bond_type',
  'increment_amount_secondary',
  'issue_date',
  'issue_increment',
  'issue_minimum',
  'issue_price',
  'issue_size',
  'issuer',
  'issuer_offset',
  'issuer_sector_rv_score',
  'last_coupon_date',
  'liquidity_institutional_aggregate',
  'liquidity_institutional_buy',
  'liquidity_institutional_sell',
  'liquidity_micro_aggregate',
  'liquidity_micro_buy',
  'liquidity_micro_sell',
  'liquidity_retail_aggregate',
  'liquidity_retail_buy',
  'liquidity_retail_sell',
  'lowest_rating',
  'market_spread_price',
  'market_spread_ytm',
  'market_spread_ytw',
  'make_whole_benchmark',
  'make_whole_callable',
  'make_whole_end_date',
  'make_whole_spread',
  'maturity_date',
  'maturity_in_years',
  'maturity_redemption_price',
  'maturity_type',
  'maturity_year',
  'workout_year',
  'min_denom_amount_secondary',
  'moodys_rating',
  'moodys_rating_date',
  'moodys_underlying_rating',
  'moodys_underlying_rating_date',
  'moodys_outlook',
  'moodys_outlook_date',
  'municipal_state',
  'muni_obligor_state',
  'municipal_taxable_federal',
  'municipal_type',
  'muni_sector',
  'muni_tax_status',
  'naics_sector',
  'naics_subsector',
  'name',
  'next_call_date',
  'next_call_in_years',
  'next_call_price',
  'next_coupon_date',
  'next_sink_date',
  'odd_first_coupon',
  'odd_last_coupon',
  'obligor',
  'original_issue_date',
  'original_issue_price',
  'original_issue_size',
  'par_value',
  'partial_call_announced',
  'perpetual',
  'prev_coupon_date',
  'principal_factor',
  'private_placement',
  'prospectus_date',
  'purpose',
  'puttable',
  'qcb_indicator',
  'rating_curve_value',
  'refund_date',
  'refund_price',
  'reg_s',
  'sector',
  'sector_offset',
  'security_code',
  'seniority',
  'seniority_adjustment',
  'seniority_type',
  'settlement_convention',
  'sink_fund_redemption_method',
  'sinking_fund',
  'sp_creditwatch',
  'sp_creditwatch_date',
  'sp_outlook',
  'sp_outlook_date',
  'sp_rating',
  'sp_rating_date',
  'sp_underlying_rating',
  'sp_underlying_rating_date',
  'status',
  'strip_type',
  'sukuk_contract_type',
  'sukuk_indicator',
  'sukuk_type',
  'survivor_option',
  'symbol',
  'ticker',
  'trace_eligible',
  'trading_restrictions_type',
  'treasury_benchmark',
  'treasury_subtype',
  'ultimate_borrower',
  'ultimate_parent',
  'ultimate_parent_ticker',
  'us_on_the_run_ind',
  'use_of_proceeds',
  'vrdo_indicator',
  'workout_date',
  'workout_in_years',
])

/**
 * The SORM `Security` field behind each live-inventory reference-data field.
 * A field absent here (`call_schedule`, `objective_score`, `filter_lists`, …)
 * has no Security equivalent.
 */
export const SECURITY_FIELD_BY_INSTRUMENT_FIELD: ReadonlyMap<
  ReferenceDataField,
  SecurityField
> = new Map([
  ...RENAMED_FIELDS,
  ...SAME_NAME_FIELDS.map((field): [ReferenceDataField, SecurityField] => [
    field,
    field,
  ]),
])
